import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { mountShell } from '../components/Shell.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { formatCrowns, formatBigNum } from '../util/formatBig.js';

/** Format a BigInt-as-string count for display (HP, damage). */
function bn(s: string): string {
  return formatBigNum(Number(s));
}
import { titleChipHtml } from '../components/Title.js';
import { showLootChanceDialog } from '../components/LootChanceDialog.js';
import { showSpoilsDialog } from '../components/ConfirmDialog.js';
import type { CryptStatusResponse } from '@rex-mortis/shared';

interface FlashState {
  kind: 'success' | 'danger' | 'warn';
  message: string;
}

let countdownTimer: number | null = null;
function clearCountdown(): void {
  if (countdownTimer !== null) {
    window.clearInterval(countdownTimer);
    countdownTimer = null;
  }
}

export async function renderCrypt(root: HTMLElement): Promise<void> {
  let status: CryptStatusResponse | null = null;
  let flash: FlashState | null = null;

  try {
    const me = await api.me();
    store.set({ account: me.account, character: me.character });
    status = await api.cryptStatus();
  } catch {
    // tolerated — page renders a graceful empty state
  }

  mountShell(root, (main) => {
    const renderMain = (): void => {
      const char = store.get().character;
      if (!char || !status || !status.boss) {
        main.innerHTML = '<p>The Crypt is sealed for the moment…</p>';
        return;
      }
      const b = status.boss;
      const you = status.you;
      const active = b.status === 'ACTIVE';
      const canStrike = active && char.status === 'OKAY' && you.vigor >= 10;
      const strikeReason =
        char.status !== 'OKAY'
          ? 'You cannot descend right now'
          : !active
            ? 'This week’s denizen has fallen'
            : you.vigor < 10
              ? 'Not enough Vigor to strike'
              : `Spend all ${you.vigor} Vigor`;

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/crypt.webp" alt="" onerror="this.style.display='none'" />
        <h1>The Crypt</h1>
        <p style="font-style: italic; color: var(--c-ink-soft); margin-top: -8px;">
          Beneath the throne, something old stirs once a week. The whole realm must put it down together.
        </p>

        <div id="flash"></div>

        ${
          you.rewardReady
            ? `<div class="panel" style="border-color: var(--c-gold);">
                 <h2>Rewards await</h2>
                 <p style="color: var(--c-ink-soft);">A denizen you struck has rewards unclaimed — your share scales with the damage you dealt. Crowns and loot come only from a denizen that <strong>fell</strong>; if it escaped the week, you still earn XP for the effort.</p>
                 <button class="btn" id="claim">Claim your share</button>
               </div>`
            : ''
        }

        <div class="panel crypt-boss">
          <div class="crypt-boss-head">
            <img class="crypt-portrait" src="/img/crypt/${b.imageSlug}.png" alt="" onerror="this.style.display='none'" />
            <div class="crypt-boss-id">
              <h2>${escapeHtml(b.name)}</h2>
              <p class="crypt-blurb">${escapeHtml(b.blurb)}</p>
              <p class="crypt-meta">
                ${
                  b.status === 'SLAIN'
                    ? '<span class="crypt-tag slain">Slain — rest until next week</span>'
                    : b.status === 'EXPIRED'
                      ? '<span class="crypt-tag">Sank back into the dark — wounds dealt are still honoured</span>'
                      : `<span class="crypt-tag active">Stalking · sinks in <span id="cd">${formatRemaining(b.expiresAt)}</span></span>`
                }
              </p>
            </div>
          </div>

          <div class="crypt-hpwrap">
            <div class="crypt-hpbar"><div class="crypt-hpfill" style="width:${b.hpPct}%"></div></div>
            <div class="crypt-hplabel">${bn(b.remainingHp)} / ${bn(b.maxHp)} HP (${b.hpPct}%)</div>
          </div>
          <div style="text-align:center; margin-top: var(--s-3);">
            <button class="btn ghost" id="view-loot">View its spoils</button>
          </div>
        </div>

        <div class="panel">
          <h2>Your strike</h2>
          <dl class="kv">
            <dt>Your Vigor</dt><dd>${you.vigor} / ${you.vigorMax}</dd>
            <dt>A full strike deals</dt><dd>${bn(you.estStrikeDamage)} damage</dd>
            <dt>Your damage so far</dt><dd>${bn(you.damage)} (${you.sharePct}% of the wound)</dd>
          </dl>
          <p style="color: var(--c-ink-soft); font-style: italic; margin-top: -4px;">
            Choose how much Vigor to spend on the blow — it defaults to all of it. There is no danger
            to you, only the cost. Vigor regrows; come back and strike again.
          </p>
          <div class="strike-row">
            <input type="number" id="strike-vigor" class="strike-vigor-input"
              value="${you.vigor}" min="10" max="${you.vigor}" step="1"
              ${canStrike ? '' : 'disabled'} aria-label="Vigor to spend on this strike" />
            <button class="btn" id="strike" title="${escapeHtml(strikeReason)}" ${canStrike ? '' : 'disabled'}>
              ${active ? 'Strike the denizen' : 'The denizen has fallen'}
            </button>
          </div>
        </div>

        <div class="panel">
          <h2>Who struck hardest</h2>
          <p style="color: var(--c-ink-soft); font-style: italic; margin-top: -4px;">
            ${status.participantCount} soul${status.participantCount === 1 ? '' : 's'} have set upon it. Fell the denizen and crowns + loot are split by damage dealt — the single hardest striker earns a rare-loot roll. Strike but fail to fell it and you still earn XP for the effort.
          </p>
          ${
            status.ladder.length === 0
              ? `<p class="empty" style="font-style:italic;color:var(--c-ink-soft)">No-one has struck yet. Be the first into the dark.</p>`
              : `<div class="crypt-ladder">${ladderHtml(status)}</div>`
          }
        </div>
      `;

      const style = document.createElement('style');
      style.textContent = CRYPT_CSS;
      main.appendChild(style);

      const flashRoot = main.querySelector<HTMLDivElement>('#flash')!;
      if (flash) {
        const div = document.createElement('div');
        div.className = `alert ${flash.kind === 'warn' ? 'danger' : flash.kind}`;
        div.textContent = flash.message;
        flashRoot.appendChild(div);
      }

      // View the boss's loot table (same popup the Misdeeds / Good Deeds use).
      const lootBtn = main.querySelector<HTMLButtonElement>('#view-loot');
      lootBtn?.addEventListener('click', () => {
        if (!status) return;
        const lt = status.loot;
        const basePct = Math.round(lt.contributor.dropChanceBase * 100);
        const maxPct = Math.round(lt.contributor.dropChanceMax * 100);
        const guarPct = Math.round(lt.contributor.guaranteedShare * 100);
        showLootChanceDialog({
          title: status.boss!.name,
          dropLabel: 'Spoils — any contributor',
          dropChance: lt.contributor.dropChanceBase,
          dropTable: lt.contributor.table,
          extraSection: {
            label: 'Top-Striker Reward (hardest striker only)',
            dropChance: lt.top.dropChance,
            dropTable: lt.top.table,
          },
          footnote:
            `Loot drops only when the denizen is FELLED. Your contributor drop chance rises with your damage share — from ${basePct}% up to ${maxPct}%, and deal ${guarPct}% or more of the wound and a drop is guaranteed. If it drops, one item is picked by its weight share below. The Top-Striker roll belongs to the single hardest striker alone.`,
        });
      });

      // Strike
      const strikeBtn = main.querySelector<HTMLButtonElement>('#strike');
      strikeBtn?.addEventListener('click', async () => {
        if (strikeBtn.disabled) return;
        strikeBtn.disabled = true;
        const vigorInput = main.querySelector<HTMLInputElement>('#strike-vigor');
        const chosen = vigorInput ? Math.floor(Number(vigorInput.value)) : NaN;
        const amount = Number.isFinite(chosen) && chosen > 0 ? chosen : undefined;
        try {
          const r = await api.cryptStrike(amount);
          store.set({ character: r.character });
          const spoilsLine =
            r.spoils && r.spoils.length > 0
              ? ` The dark yields ${r.spoils.map((s) => `${s.name} ×${s.qty}`).join(', ')}.`
              : '';
          flash = {
            kind: 'success',
            message: r.slain
              ? `You strike for ${bn(r.damageDealt)} — and the denizen falls! Claim your share below.${spoilsLine}`
              : `You strike for ${bn(r.damageDealt)} damage. (${r.hpPct}% HP remains.)${spoilsLine}`,
          };
          status = await api.cryptStatus();
        } catch (err) {
          flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Your strike falters.' };
        } finally {
          anchored(renderMain);
        }
      });

      // Claim
      const claimBtn = main.querySelector<HTMLButtonElement>('#claim');
      claimBtn?.addEventListener('click', async () => {
        claimBtn.disabled = true;
        try {
          const r = await api.cryptClaim();
          store.set({ character: r.character });
          // Full per-item spoils popup so a boss kill's loot is clearly listed
          // (a single toast scrolled the items past too fast to read).
          const items = r.rewards.map((rw): import('../components/ConfirmDialog.js').SpoilsItem => {
            if (rw.kind === 'crowns') return { label: `${formatCrowns(String(rw.amount))} crowns`, glyph: '🪙' };
            if (rw.kind === 'xp') return { label: `${rw.amount.toLocaleString()} XP`, glyph: '✦' };
            const label = `${rw.label}${rw.amount > 1 ? ` ×${rw.amount}` : ''}`;
            return rw.itemSlug ? { label, itemSlug: rw.itemSlug } : { label };
          });
          status = await api.cryptStatus();
          flash = null;
          anchored(renderMain);
          await showSpoilsDialog({
            title: r.wasTopDamage ? 'Top Striker — Spoils of the Crypt' : 'Spoils of the Crypt',
            subtitle: r.wasTopDamage
              ? 'You dealt the heaviest blow — a rare-loot roll was yours.'
              : 'Your share of the fallen denizen, by the damage you dealt.',
            items,
          });
          return;
        } catch (err) {
          flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Nothing to claim.' };
        } finally {
          anchored(renderMain);
        }
      });

      // Live "sinks in" countdown.
      clearCountdown();
      if (active) {
        countdownTimer = window.setInterval(() => {
          const cd = main.querySelector<HTMLSpanElement>('#cd');
          if (!cd) {
            clearCountdown();
            return;
          }
          cd.textContent = formatRemaining(b.expiresAt);
        }, 1000);
      }
    };

    anchored(renderMain);
  });

  window.addEventListener('hashchange', clearCountdown, { once: true });
}

function ladderHtml(status: CryptStatusResponse): string {
  return status.ladder
    .map(
      (r) => `
      <div class="crypt-row ${r.isYou ? 'you' : ''}">
        <span class="crypt-rank">#${r.rank}</span>
        <span class="crypt-name">${escapeHtml(r.name)}${r.isYou ? ' <em>(you)</em>' : ''}</span>
        <span class="crypt-dmg">${bn(r.damage)}</span>
        <span class="crypt-share">${r.sharePct}%</span>
      </div>`,
    )
    .join('');
}

function formatRemaining(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.parse(iso) - Date.now();
  if (ms <= 0) return 'now';
  const sec = Math.ceil(ms / 1000);
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const CRYPT_CSS = `
  .crypt-boss-head { display: flex; flex-direction: column; align-items: center; text-align: center; gap: var(--s-3); }
  .crypt-portrait {
    width: 100%; aspect-ratio: 1 / 1; object-fit: cover; display: block;
    border: 1px solid var(--c-rule); border-radius: var(--radius);
    background: var(--c-parchment-dark, var(--c-parchment));
    box-shadow: 0 6px 22px rgba(0, 0, 0, 0.4);
  }
  .crypt-boss-id { min-width: 0; max-width: 640px; }
  .crypt-blurb { font-style: italic; color: var(--c-ink-soft); margin: 4px 0 6px 0; }
  .crypt-meta { margin: 0; }
  .crypt-tag {
    font-size: 0.8rem; font-family: var(--font-numeric);
    padding: 2px 8px; border-radius: 999px;
    background: var(--c-rule-soft); color: var(--c-ink-soft);
  }
  .crypt-tag.active { background: rgba(120,20,20,0.12); color: var(--c-oxblood); }
  .crypt-tag.slain { background: rgba(20,90,40,0.14); color: #1f6b34; }
  .crypt-hpwrap { margin-top: var(--s-3); }
  .crypt-hpbar {
    height: 22px; border-radius: 999px; overflow: hidden;
    background: var(--c-rule-soft); border: 1px solid var(--c-rule);
  }
  .crypt-hpfill {
    height: 100%;
    background: linear-gradient(90deg, #6b0f12, #b22025);
    transition: width 0.4s ease;
  }
  .crypt-hplabel {
    text-align: center; font-family: var(--font-numeric);
    font-size: 0.86rem; color: var(--c-ink-soft); margin-top: 4px;
  }
  .crypt-ladder { display: flex; flex-direction: column; gap: 2px; }
  .crypt-row {
    display: grid; grid-template-columns: 44px 1fr auto 56px;
    gap: var(--s-2); align-items: baseline;
    padding: 5px var(--s-2); border-radius: var(--radius);
  }
  .crypt-row.you { background: rgba(180,140,40,0.14); }
  .crypt-rank { font-family: var(--font-numeric); color: var(--c-ink-soft); }
  .crypt-name { font-family: var(--font-display); }
  .crypt-dmg { font-family: var(--font-numeric); color: var(--c-oxblood); text-align: right; }
  .crypt-share { font-family: var(--font-numeric); color: var(--c-ink-soft); text-align: right; }
  .strike-row { display: flex; gap: var(--s-2); align-items: center; flex-wrap: wrap; }
  /* Layout only — colours come from the global pinned cream cell in pages.css
     (dark ink on cream, both themes). Don't set background/color here: theme
     tokens can resolve dark-on-dark in some themes (the foreign-shop qty bug). */
  .strike-vigor-input {
    width: 130px; padding: 9px 12px; font-family: var(--font-numeric); font-size: 1rem;
    border: 1px solid rgba(120, 80, 40, 0.45); border-radius: var(--radius);
  }
  .strike-vigor-input:focus { outline: none; border-color: var(--c-oxblood); }
`;
